/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  FieldMatch,
  FieldMatch$inboundSchema,
  FieldMatch$Outbound,
  FieldMatch$outboundSchema,
} from "./fieldmatch.js";
import {
  MethodMatch,
  MethodMatch$inboundSchema,
  MethodMatch$Outbound,
  MethodMatch$outboundSchema,
} from "./methodmatch.js";
import {
  PathMatch,
  PathMatch$inboundSchema,
  PathMatch$Outbound,
  PathMatch$outboundSchema,
} from "./pathmatch.js";
import {
  RemoteIpMatch,
  RemoteIpMatch$inboundSchema,
  RemoteIpMatch$Outbound,
  RemoteIpMatch$outboundSchema,
} from "./remoteipmatch.js";

/**
 * A single request match expression. Exactly one of `path`, `method`,
 *
 * @remarks
 * `header`, `queryParam` or `remoteIp` must be set.
 */
export type MatchExpr = {
  /**
   * Matches on the request path.
   */
  path?: PathMatch | undefined;
  /**
   * Matches when the request method is one of the listed methods.
   */
  method?: MethodMatch | undefined;
  /**
   * Matches a named request field (header or query parameter). Exactly one of
   *
   * @remarks
   * `present` or `value` must be set.
   */
  header?: FieldMatch | undefined;
  /**
   * Matches a named request field (header or query parameter). Exactly one of
   *
   * @remarks
   * `present` or `value` must be set.
   */
  queryParam?: FieldMatch | undefined;
  /**
   * Matches the remote IP against IPv4 or IPv6 CIDR ranges. Exactly one of `in`
   *
   * @remarks
   * or `notIn` must be set. Entries are rejected if they have host bits set (such
   * as `10.1.2.3/8`), are IPv4-mapped IPv6 addresses, or carry a zone. Single
   * addresses are returned as full-length prefixes, such as `203.0.113.7/32`.
   */
  remoteIp?: RemoteIpMatch | undefined;
};

/** @internal */
export const MatchExpr$inboundSchema: z.ZodType<
  MatchExpr,
  z.ZodTypeDef,
  unknown
> = z.object({
  path: PathMatch$inboundSchema.optional(),
  method: MethodMatch$inboundSchema.optional(),
  header: FieldMatch$inboundSchema.optional(),
  queryParam: FieldMatch$inboundSchema.optional(),
  remoteIp: RemoteIpMatch$inboundSchema.optional(),
});
/** @internal */
export type MatchExpr$Outbound = {
  path?: PathMatch$Outbound | undefined;
  method?: MethodMatch$Outbound | undefined;
  header?: FieldMatch$Outbound | undefined;
  queryParam?: FieldMatch$Outbound | undefined;
  remoteIp?: RemoteIpMatch$Outbound | undefined;
};

/** @internal */
export const MatchExpr$outboundSchema: z.ZodType<
  MatchExpr$Outbound,
  z.ZodTypeDef,
  MatchExpr
> = z.object({
  path: PathMatch$outboundSchema.optional(),
  method: MethodMatch$outboundSchema.optional(),
  header: FieldMatch$outboundSchema.optional(),
  queryParam: FieldMatch$outboundSchema.optional(),
  remoteIp: RemoteIpMatch$outboundSchema.optional(),
});

export function matchExprToJSON(matchExpr: MatchExpr): string {
  return JSON.stringify(MatchExpr$outboundSchema.parse(matchExpr));
}
export function matchExprFromJSON(
  jsonString: string,
): SafeParseResult<MatchExpr, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => MatchExpr$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'MatchExpr' from JSON`,
  );
}
